// src/utils/richtext/richTextContent.js
// 判断「一段(可能是富文本 HTML 的)说明有没有可见内容」+ 取其可见纯文本。
// 管理端富文本编辑器对空说明常存 <p></p> / <p><br></p> / <p>&nbsp;</p> 之类残留;
// 这些字面上非空、可见内容却为空。前端若直接 {{ }} 插值,会把 <p></p> 当文本
// 显示成字面「<p>」;先经此判空即可整段不渲染。
//
// 与 sanitizeRichText 同源(都靠 DOMParser 解析,不执行脚本 / 不加载资源);
// sanitizeRichText 负责「非空时安全 v-html」,本文件负责「判空 / 取纯文本」。
// 移植自 frontend-kiosk-2026 src/features/richtext/richTextContent.ts。
import { DANGEROUS_CONTAINER_SELECTOR } from './sanitizeRichText'

// 折叠空白 + 判空用的空白字符类。\s 覆盖普通空白(空格 / 制表 / 换行 …),但不含这些
// “看不见却非空白”的残留:U+00A0 &nbsp;、U+200B 零宽空格、U+FEFF 零宽不断行空格 / BOM ——
// 富文本编辑器对空说明常留这些。不含 U+200C/U+200D(ZWNJ/ZWJ):它们是表情 / 文字的语义
// 连接符,剥掉会拆坏家庭表情之类。用 fromCharCode 构造,保证源码全 ASCII、不埋看不见的字符。
const INVISIBLE_WS = String.fromCharCode(0x00A0, 0x200B, 0xFEFF)
const COLLAPSE_WS_RE = new RegExp('[\\s' + INVISIBLE_WS + ']+', 'g')

// 解析成 body 并去掉 script/style/iframe/svg 等危险容器——复用 sanitizeRichText 的同一份容器名单,
// 保证「取纯文本 / 判空」与「净化后 v-html」对同一段富文本一致(否则 <iframe>x</iframe> 会在净化那档
// 隐藏、在纯文本那档露出);这些容器的源码文本也不该被当可见内容泄露。
function parseVisibleBody(html) {
  if (html == null) return null
  const str = String(html)
  if (str.trim() === '') return null
  const doc = new DOMParser().parseFromString(str, 'text/html')
  const body = doc.body
  if (!body) return null
  body.querySelectorAll(DANGEROUS_CONTAINER_SELECTOR).forEach((el) => el.remove())
  return body
}

/**
 * 取富文本里的可见纯文本:剥掉所有标签、去掉 &nbsp;、折叠连续空白、首尾去空。
 * 适合不需要排版、只要一段文字的地方。null/undefined/空/纯残留标签 → 空串。
 * @param {string|null|undefined} html
 * @returns {string}
 */
export function richTextToPlainText(html) {
  const body = parseVisibleBody(html)
  if (!body) return ''
  const text = body.textContent || ''
  return text.replace(COLLAPSE_WS_RE, ' ').trim()
}

/**
 * 这段富文本有没有可见内容。
 * 有可见文字 → true;空 / 纯空白 / <p></p> / <p><br></p> / <p>&nbsp;</p> / 只有脚本源码 → false。
 * @param {string|null|undefined} html
 * @returns {boolean}
 */
export function richTextHasContent(html) {
  return richTextToPlainText(html) !== ''
}
